import { useParams, useNavigate } from "react-router-dom";
import {
  useV1ProductionBomFamiliesRetrieve,
  useV1ProductionBomRevisionsList,
  useV1ProductionBomRevisionsCreate,
} from "@/api/generated/v1/v1";
import { usePermission } from "@/auth/usePermission";
import { StatusBadge } from "@/shared/components/StatusBadge";
import { PageBanner } from "@/shared/components/PageBanner";
import { useQueryClient } from "@tanstack/react-query";

export function BomFamilyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canManage = usePermission("production.manage");

  const { data: family, isLoading: familyLoading } = useV1ProductionBomFamiliesRetrieve(id!);
  const { data: revisions, isLoading: revisionsLoading } = useV1ProductionBomRevisionsList({
    family: id,
    page_size: 200,
  } as any);
  const { mutateAsync: createRevision, isPending: isCreatingRevision } = useV1ProductionBomRevisionsCreate();

  const handleCreateDraft = async () => {
    try {
      const res = await createRevision({ data: { family: id! } } as any);
      queryClient.invalidateQueries({ queryKey: ["v1ProductionBomRevisionsList"] });
      navigate(`/bom/revisions/${(res as any).id}`);
    } catch (err: any) {
      alert("خطا در ایجاد ریویژن: " + (err.response?.data?.detail || ""));
    }
  };

  if (familyLoading) {
    return (
      <div style={{ padding: "3rem", textAlign: "center" }}>
        <span style={{ fontSize: "1.5rem" }}>⏳</span>
        <p style={{ color: "var(--color-text-muted, #666)", fontWeight: 700, marginTop: "0.5rem" }}>
          در حال بارگذاری اطلاعات خانواده BOM...
        </p>
      </div>
    );
  }

  if (!family) {
    return (
      <div style={{ padding: "2rem" }}>
        <div
          style={{
            padding: "1.25rem",
            background: "rgba(239,68,68,0.1)",
            borderRight: "4px solid #EF4444",
            borderRadius: "10px",
          }}
        >
          <p style={{ color: "#EF4444", fontWeight: 800, margin: 0 }}>خانواده BOM مورد نظر یافت نشد.</p>
        </div>
      </div>
    );
  }

  const outputMat = family?.output_material_revision_display;
  const revisionsList = revisions?.results || [];
  const publishedRevCount = revisionsList.filter((r: any) => r.status === "published").length;

  return (
    <div
      dir="rtl"
      style={{
        padding: "0.5rem 0",
        maxWidth: "100%",
        margin: "0",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      {/* 1. STANDARDIZED PAGE BANNER */}
      <PageBanner
        title={`مدیریت خانواده محصول: ${family.name}`}
        subtitle={`کد: ${family.code} | در این بخش نسخه‌های ساخت (Revisions) این محصول را مدیریت کنید`}
        icon="🗂️"
        helpLink="/help/bom/families/detail"
      >
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
          {canManage && (
            <button
              onClick={handleCreateDraft}
              disabled={isCreatingRevision}
              style={{
                padding: "0.6rem 1.25rem",
                borderRadius: "12px",
                background: "#ffffff",
                color: "#007270",
                fontWeight: "800",
                fontSize: "0.9rem",
                border: "1px solid rgba(255, 255, 255, 0.8)",
                cursor: "pointer",
                boxShadow: "0 4px 14px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.2s ease",
              }}
            >
              <span>{isCreatingRevision ? "⏳" : "➕"}</span>
              <span>{isCreatingRevision ? "در حال ایجاد..." : "ایجاد ریویژن جدید (Draft)"}</span>
            </button>
          )}

          <button
            onClick={() => navigate("/bom/families")}
            style={{
              padding: "0.6rem 1.1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: "700",
              fontSize: "0.9rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              transition: "all 0.2s ease",
            }}
          >
            <span>←</span>
            <span>بازگشت به لیست خانواده‌ها</span>
          </button>
        </div>
      </PageBanner>

      {/* 2. STATS REPORT GRID (KPI CARDS) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.25rem",
        }}
      >
        {/* CARD 1: BOM FAMILY CODE */}
        <div className="brand-card kpi-card">
          <span
            style={{
              fontSize: "1.8rem",
              background: "rgba(0,114,112,0.08)",
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            🏷️
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              کد خانواده BOM
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span
                dir="ltr"
                style={{
                  fontFamily: "monospace",
                  fontSize: "1.1rem",
                  fontWeight: "800",
                  color: "#007270",
                }}
              >
                {family.code}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#007270" }}>شناسه</span>
            </div>
          </div>
        </div>

        {/* CARD 2: OUTPUT MATERIAL */}
        <div className="brand-card kpi-card">
          <span
            style={{
              fontSize: "1.8rem",
              background: "rgba(245,158,11,0.08)",
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            📦
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              محصول خروجی
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1rem", fontWeight: "800", color: "#D4AF37", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {outputMat ? outputMat.name : "بدون محصول"}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: outputMat ? "#10B981" : "#EF4444" }}>
                {outputMat ? "نگاشت شده" : "تعریف‌نشده"}
              </span>
            </div>
          </div>
        </div>

        {/* CARD 3: TOTAL REVISIONS */}
        <div className="brand-card kpi-card">
          <span
            style={{
              fontSize: "1.8rem",
              background: "rgba(59,130,246,0.08)",
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            ⚡
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              کل ریویژن‌ها (نسخه‌ها)
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.3rem", fontWeight: "800", color: "#3B82F6" }}>
                {revisionsLoading ? "..." : revisionsList.length.toLocaleString("fa-IR")}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#3B82F6" }}>نسخه</span>
            </div>
          </div>
        </div>

        {/* CARD 4: PUBLISHED REVISIONS */}
        <div className="brand-card kpi-card">
          <span
            style={{
              fontSize: "1.8rem",
              background: "rgba(16,185,129,0.08)",
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            ✅
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              نسخه‌های منتشرشده
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.3rem", fontWeight: "800", color: "#10B981" }}>
                {revisionsLoading ? "..." : publishedRevCount.toLocaleString("fa-IR")}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#10B981" }}>آماده بهره‌برداری</span>
            </div>
          </div>
        </div>
      </div>

      {/* 3. FAMILY SPECIFICATION CARD */}
      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.6rem",
            marginBottom: "1.25rem",
            borderBottom: "1px solid var(--color-border, #E4DCC8)",
            paddingBottom: "0.75rem",
          }}
        >
          <span style={{ fontSize: "1.3rem" }}>📋</span>
          <div>
            <h2 style={{ fontSize: "1.05rem", fontWeight: "800", color: "#007270", margin: 0 }}>
              مشخصات عمومی و کالا خروجی خانواده
            </h2>
            <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted, #666)" }}>
              اطلاعات اصلی شناسه خانواده BOM و محصول نهایی نگاشت‌شده
            </span>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.5rem" }}>
          <div>
            <span style={{ fontSize: "0.82rem", color: "var(--color-text-muted, #666)", fontWeight: 700 }}>
              کد خانواده BOM
            </span>
            <div style={{ marginTop: "0.4rem" }}>
              <span
                dir="ltr"
                style={{
                  fontFamily: "monospace",
                  fontWeight: 800,
                  color: "#007270",
                  background: "rgba(0, 114, 112, 0.08)",
                  padding: "0.35rem 0.8rem",
                  borderRadius: "8px",
                  fontSize: "0.95rem",
                  display: "inline-block",
                }}
              >
                {family.code}
              </span>
            </div>
          </div>

          <div>
            <span style={{ fontSize: "0.82rem", color: "var(--color-text-muted, #666)", fontWeight: 700 }}>
              نام خانواده BOM
            </span>
            <div style={{ marginTop: "0.4rem", fontWeight: 800, fontSize: "1rem", color: "#2B2B2B" }}>
              {family.name}
            </div>
          </div>

          <div>
            <span style={{ fontSize: "0.82rem", color: "var(--color-text-muted, #666)", fontWeight: 700 }}>
              محصول خروجی نگاشت‌شده
            </span>
            <div style={{ marginTop: "0.4rem" }}>
              {outputMat ? (
                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                  <span style={{ fontWeight: 800, fontSize: "0.95rem", color: "#2B2B2B" }}>
                    {outputMat.sku || outputMat.code ? `[${outputMat.sku || outputMat.code}] ` : ""}
                    {outputMat.name}
                  </span>
                  <span
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      background: "rgba(245, 158, 11, 0.12)",
                      color: "#D4AF37",
                      padding: "0.15rem 0.5rem",
                      borderRadius: "6px",
                      border: "1px solid rgba(245, 158, 11, 0.2)",
                    }}
                  >
                    نسخه {outputMat.revision_no || "۱"}
                  </span>
                </div>
              ) : (
                <span style={{ color: "#9CA3AF", fontSize: "0.88rem", fontWeight: 600 }}>— بدون محصول خروجی —</span>
              )}
            </div>
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <span style={{ fontSize: "0.82rem", color: "var(--color-text-muted, #666)", fontWeight: 700 }}>
              توضیحات تکمیلی
            </span>
            <div style={{ marginTop: "0.4rem", fontSize: "0.9rem", color: "#4B5563", fontWeight: 600 }}>
              {family.description || "بدون توضیحات ثبت‌شده."}
            </div>
          </div>
        </div>
      </div>

      {/* 4. REVISIONS LIST TABLE CARD */}
      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1.25rem",
            borderBottom: "1px solid var(--color-border, #E4DCC8)",
            paddingBottom: "0.75rem",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <span style={{ fontSize: "1.3rem" }}>⚡</span>
            <div>
              <h2 style={{ fontSize: "1.05rem", fontWeight: "800", color: "#007270", margin: 0 }}>
                لیست ریویژن‌ها (نسخه‌های) BOM
              </h2>
              <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted, #666)" }}>
                تاریخچه نسخه، وضعیت انتشار و مدیریت اقلام فرمول ساخت
              </span>
            </div>
          </div>

          {canManage && (
            <button
              onClick={handleCreateDraft}
              disabled={isCreatingRevision}
              style={{
                padding: "0.5rem 1.1rem",
                borderRadius: "10px",
                background: "#007270",
                color: "#FFFFFF",
                fontSize: "0.85rem",
                fontWeight: 800,
                border: "none",
                cursor: "pointer",
                boxShadow: "0 3px 10px rgba(0, 114, 112, 0.25)",
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                transition: "all 0.2s ease",
              }}
            >
              <span>{isCreatingRevision ? "⏳" : "➕"}</span>
              <span>{isCreatingRevision ? "در حال ایجاد..." : "ایجاد ریویژن جدید (Draft)"}</span>
            </button>
          )}
        </div>

        {revisionsLoading ? (
          <div style={{ padding: "3rem", textAlign: "center" }}>
            <span style={{ fontSize: "1.5rem" }}>⏳</span>
            <p style={{ color: "var(--color-text-muted, #666)", fontWeight: 700, marginTop: "0.5rem" }}>
              در حال بارگذاری نسخه‌ها...
            </p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", textAlign: "right", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--color-border, #E4DCC8)", color: "#007270" }}>
                  <th style={{ padding: "0.9rem 1rem", fontWeight: "800", fontSize: "0.9rem" }}>شماره نسخه</th>
                  <th style={{ padding: "0.9rem 1rem", fontWeight: "800", fontSize: "0.9rem" }}>وضعیت انتشار</th>
                  <th style={{ padding: "0.9rem 1rem", fontWeight: "800", fontSize: "0.9rem" }}>یادداشت نسخه</th>
                  <th style={{ padding: "0.9rem 1rem", fontWeight: "800", fontSize: "0.9rem", textAlign: "left" }}>
                    عملیات
                  </th>
                </tr>
              </thead>
              <tbody>
                {revisionsList.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ padding: "3rem", textAlign: "center", color: "var(--color-text-muted)" }}>
                      <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>📜</div>
                      <span style={{ fontWeight: 700, fontSize: "0.95rem" }}>
                        هیچ ریویژنی برای این خانواده تعریف نشده است.
                      </span>
                    </td>
                  </tr>
                ) : (
                  revisionsList.map((rev: any) => (
                    <tr
                      key={rev.id}
                      style={{
                        borderBottom: "1px solid var(--color-border, #E4DCC8)",
                        transition: "background 0.2s ease",
                      }}
                    >
                      <td style={{ padding: "0.9rem 1rem" }}>
                        <span
                          dir="ltr"
                          style={{
                            fontFamily: "monospace",
                            fontWeight: 800,
                            color: "#007270",
                            background: "rgba(0, 114, 112, 0.08)",
                            padding: "0.25rem 0.65rem",
                            borderRadius: "6px",
                            fontSize: "0.9rem",
                            display: "inline-block",
                          }}
                        >
                          v{rev.revision_no || rev.revision_number}
                        </span>
                      </td>
                      <td style={{ padding: "0.9rem 1rem" }}>
                        <StatusBadge status={rev.status as string} />
                      </td>
                      <td style={{ padding: "0.9rem 1rem", color: "var(--color-text-main, #2B2B2B)", fontSize: "0.88rem" }}>
                        {rev.notes || rev.revision_note || "—"}
                      </td>
                      <td style={{ padding: "0.9rem 1rem", textAlign: "left" }}>
                        <button
                          onClick={() => navigate(`/bom/revisions/${rev.id}`)}
                          style={{
                            padding: "0.45rem 1rem",
                            borderRadius: "10px",
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            border: "1px solid #007270",
                            background: "transparent",
                            color: "#007270",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.4rem",
                          }}
                        >
                          <span>{rev.status === "draft" ? "ویرایش آیتم‌ها" : "مشاهده"}</span>
                          <span>←</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
